import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, NavLink, useSearchParams } from "react-router-dom";
import { Trash2, Bell, Pencil, Check, X, LifeBuoy, ShieldCheck, Camera, Glasses } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ProductCard } from "@/components/ProductCard";
import { FramePassport } from "@/components/FramePassport";
import { toast } from "sonner";

function AccountNav() {
  const items = [
    { to: "/account", label: "Overview", end: true },
    { to: "/account/orders", label: "Orders" },
    { to: "/account/wishlist", label: "Wishlist" },
    { to: "/account/compare", label: "Compare" },
    { to: "/account/sizes", label: "Saved Sizes" },
    { to: "/account/searches", label: "Saved Searches" },
    { to: "/account/notifications", label: "Notifications" },
    { to: "/account/passports", label: "Frame Passports" },
    { to: "/account/photos", label: "My Photos" },
    { to: "/account/credits", label: "Store Credit" },
    { to: "/account/referrals", label: "Refer a Friend" },
    { to: "/account/submissions", label: "Sell / Trade-In" },
  ];
  return (
    <nav className="flex lg:flex-col gap-4 lg:gap-1 mb-8 lg:mb-0 lg:w-48 overflow-x-auto">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          className={({ isActive }) => `text-sm font-heading uppercase tracking-wide py-2 whitespace-nowrap ${isActive ? "text-teal-700 border-b-2 lg:border-b-0 lg:border-l-2 border-teal-600 lg:pl-3" : "text-zinc-400 hover:text-zinc-800"}`}
          data-testid={`account-nav-${i.label.toLowerCase().replace(/\s+\/?\s*/g, "-")}`}
        >
          {i.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Overview() {
  const { user, logout } = useAuth();
  const [balance, setBalance] = useState(0);
  useEffect(() => {
    api.get("/credits/balance").then((res) => setBalance(res.data.balance));
  }, []);
  return (
    <div data-testid="account-overview">
      <h1 className="font-serif text-3xl mb-6 text-zinc-900">
        Welcome back, {user.name}
      </h1>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="border border-zinc-200 rounded-sm bg-white p-5">
          <div className="text-xs text-zinc-400 font-heading uppercase mb-1">Lookers Credit</div>
          <div className="font-serif text-2xl text-teal-700" data-testid="credit-balance">{money(balance)}</div>
        </div>
        <div className="border border-zinc-200 rounded-sm bg-white p-5">
          <div className="text-xs text-zinc-400 font-heading uppercase mb-1">Email</div>
          <div className="text-sm text-zinc-800">{user.email}</div>
        </div>
      </div>
      <button onClick={logout} className="text-xs underline text-zinc-400 hover:text-teal-700" data-testid="logout-button">Log out</button>
    </div>
  );
}

const RETURN_REASONS = [
  { value: "arrived_damaged", label: "Arrived damaged" },
  { value: "not_as_described", label: "Item materially different from description" },
  { value: "authenticity_concern", label: "Authenticity concern" },
  { value: "incorrect_item", label: "Incorrect item received" },
  { value: "measurement_issue", label: "Measurement / product information issue" },
  { value: "other_problem", label: "Other product problem" },
  { value: "change_of_mind", label: "Change of mind" },
  { value: "other", label: "Other" },
];

function OrderHelpModal({ order, onClose }) {
  const [productId, setProductId] = useState(order.items?.[0]?.product_id || "");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.url]);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!reason) { toast.error("Please choose a reason"); return; }
    if (!description.trim()) { toast.error("Please describe the issue"); return; }
    setSubmitting(true);
    try {
      await api.post("/account/returns", { order_id: order.id, product_id: productId || null, reason, description: description.trim(), photos });
      toast.success("Your request has been submitted — we'll be in touch");
      onClose(true);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="order-help-modal" onClick={() => onClose(false)}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-neutral-100 sticky top-0 bg-white">
          <h2 className="font-heading font-extrabold text-lg text-neutral-900">Get Help With {order.order_number}</h2>
          <button onClick={() => onClose(false)} className="text-neutral-400 hover:text-neutral-700" aria-label="Close" data-testid="order-help-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {order.items?.length > 1 && (
            <div>
              <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">Which item?</label>
              <select value={productId} onChange={(e) => setProductId(e.target.value)} className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm focus:border-teal-600 outline-none" data-testid="order-help-product-select">
                <option value="">Whole order</option>
                {order.items.map((it) => <option key={it.product_id} value={it.product_id}>{it.brand_name} {it.model}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">What's the issue?</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm focus:border-teal-600 outline-none" data-testid="order-help-reason-select">
              <option value="">Choose a reason…</option>
              {RETURN_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {reason === "change_of_mind" && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3" data-testid="order-help-change-of-mind-note">
              We generally don't accept change-of-mind returns on unique preloved frames. This never limits your rights under Australian Consumer Law — <Link to="/returns" className="underline">see our Returns policy</Link>.
            </p>
          )}
          <div>
            <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">Tell us more</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Describe what's happened…" className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm focus:border-teal-600 outline-none" data-testid="order-help-description-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 font-heading uppercase block mb-2">Photos (optional)</label>
            <div className="flex flex-wrap gap-2 items-center">
              {photos.map((url, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover rounded-md border border-neutral-200" />
              ))}
              <label className="w-16 h-16 border border-dashed border-neutral-300 rounded-md flex items-center justify-center text-neutral-400 hover:border-teal-600 hover:text-teal-600 cursor-pointer transition-colors" data-testid="order-help-photo-upload">
                <Camera size={18} />
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} />
              </label>
            </div>
          </div>
          <button onClick={submit} disabled={submitting} className="w-full bg-teal-600 hover:bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wide py-3.5 rounded-full transition-colors disabled:opacity-50" data-testid="order-help-submit">
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [helpOrder, setHelpOrder] = useState(null);
  const loadRequests = () => api.get("/account/returns").then((res) => setRequests(res.data)).catch(() => {});
  useEffect(() => {
    api.get("/orders").then((res) => setOrders(res.data));
    loadRequests();
  }, []);
  const requestFor = (orderId) => requests.filter((r) => r.order_id === orderId);
  return (
    <div data-testid="account-orders">
      <h1 className="font-serif text-3xl mb-6 text-zinc-900">
        Your Orders
      </h1>
      {orders.length === 0 ? <p className="text-sm text-zinc-400">No orders yet.</p> : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="border border-zinc-200 rounded-sm bg-white p-4" data-testid={`order-${o.order_number}`}>
              <div className="flex justify-between text-sm font-heading uppercase tracking-wide mb-2 text-zinc-900">
                <span>{o.order_number}</span>
                <span>{o.status}</span>
              </div>
              <div className="text-xs text-zinc-400 mb-2">{formatDate(o.created_at)}</div>
              <div className="text-xs font-heading uppercase tracking-wide text-teal-700 mb-2" data-testid={`order-fulfilment-${o.order_number}`}>
                {o.fulfilment_method === "pickup" ? "Pickup — Perth" : "Delivery"}
              </div>
              {o.fulfilment_method === "pickup" && o.pickup_address && (
                <div className="text-xs text-zinc-500 bg-teal-50 rounded-sm p-3 mb-2" data-testid={`order-pickup-address-${o.order_number}`}>
                  {o.pickup_address}
                </div>
              )}
              {o.items.map((it) => (
                <div key={it.product_id} className="text-sm text-zinc-800">{it.brand_name} {it.model} — {money(it.price)}</div>
              ))}
              <div className="text-sm font-heading mt-2 text-zinc-900">Total: {money(o.total)}</div>
              {o.tracking_number && <div className="text-xs text-zinc-400">Tracking: {o.tracking_number}</div>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                <button onClick={() => setHelpOrder(o)} className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wide text-teal-700 hover:text-teal-800 transition-colors" data-testid={`order-get-help-${o.order_number}`}>
                  <LifeBuoy size={14} /> Get Help With This Order
                </button>
              </div>
              {requestFor(o.id).map((r) => (
                <div key={r.id} className="mt-2 text-xs bg-neutral-50 border border-neutral-100 rounded-md p-2.5 text-neutral-600" data-testid={`order-request-${r.request_number}`}>
                  <span className="font-heading font-bold text-neutral-800">{r.request_number}</span> · {r.reason_label} · <span className="uppercase tracking-wide text-teal-700">{r.status}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {helpOrder && <OrderHelpModal order={helpOrder} onClose={(submitted) => { setHelpOrder(null); if (submitted) loadRequests(); }} />}
    </div>
  );
}

function Wishlist() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/wishlist").then((res) => setItems(res.data));
  }, []);
  const handleWishlistChange = (productId, isSaved) => {
    if (!isSaved) setItems((prev) => prev.filter((p) => p.id !== productId));
  };
  const tryOnable = items.filter((p) => p.try_on_asset_url);
  return (
    <div data-testid="account-wishlist">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-3xl text-zinc-900">
          Your Wishlist
        </h1>
        {tryOnable.length > 0 && (
          <Link
            to={`/try-on/${tryOnable[0].id}?source=wishlist`}
            className="inline-flex items-center gap-2 bg-charcoal text-white text-xs font-heading font-bold uppercase tracking-wide px-6 py-3.5 rounded-full hover:bg-teal-600 transition-colors"
            data-testid="wishlist-try-on-all"
          >
            <Glasses size={14} /> Try On Your Saved Frames ({tryOnable.length})
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-center py-10" data-testid="wishlist-empty-state">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/lookers/branding/doodle_empty_heart.png`} alt="" className="w-20 h-20 object-contain mx-auto mb-4" />
          <p className="text-sm text-zinc-400">Save your favourite frames to see them here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {items.map((p) => (
            <ProductCard key={p.id} product={p} wishlisted onWishlistChange={handleWishlistChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function Credits() {
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  useEffect(() => {
    api.get("/credits/balance").then((res) => setBalance(res.data.balance));
    api.get("/credits/transactions").then((res) => setTxns(res.data));
  }, []);
  return (
    <div data-testid="account-credits">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Lookers Credit
      </h1>
      <div className="font-serif text-4xl mb-8 text-teal-700" data-testid="credit-balance">{money(balance)}</div>
      {txns.length === 0 ? <p className="text-sm text-zinc-400">No transactions yet.</p> : (
        <div className="space-y-2">
          {txns.map((t) => (
            <div key={t.id} className="flex justify-between text-sm border-b border-zinc-100 pb-2 text-zinc-800">
              <span>{t.source.replace(/_/g, " ")} · {formatDate(t.created_at)}</span>
              <span className={t.amount < 0 ? "text-rose-600" : "text-emerald-600"}>{t.amount > 0 ? "+" : ""}{money(t.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Referrals() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/referrals/me").then((res) => setData(res.data));
  }, []);
  if (!data) return <div className="text-sm text-zinc-400" data-testid="account-referrals">Loading…</div>;
  const link = `${window.location.origin}/register?ref=${data.referral_code}`;
  const copyLink = () => {
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };
  return (
    <div data-testid="account-referrals">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Refer a Friend
      </h1>
      <p className="text-sm text-zinc-500 mb-8">
        Share your link. When a friend makes their first purchase, you get {money(data.reward_referrer)} in Lookers Credit and they get {money(data.reward_referee)}.
      </p>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="border border-zinc-200 rounded-sm bg-white p-5">
          <div className="text-xs text-zinc-400 font-heading uppercase mb-1">Your Referral Code</div>
          <div className="font-serif text-2xl text-teal-700" data-testid="referral-code">{data.referral_code}</div>
        </div>
        <div className="border border-zinc-200 rounded-sm bg-white p-5">
          <div className="text-xs text-zinc-400 font-heading uppercase mb-1">Credit Earned</div>
          <div className="font-serif text-2xl text-teal-700" data-testid="referral-total-earned">{money(data.total_earned)}</div>
        </div>
      </div>
      <div className="flex gap-2 mb-10">
        <input readOnly value={link} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm flex-1 text-zinc-900 rounded-sm" data-testid="referral-link-input" />
        <button onClick={copyLink} className="bg-teal-600 hover:bg-teal-700 text-ink text-xs px-5 py-2.5 font-heading font-semibold uppercase rounded-full transition-colors" data-testid="copy-referral-link-button">Copy Link</button>
      </div>
      <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">Friends You've Referred ({data.referred_count})</h2>
      {data.referrals.length === 0 ? <p className="text-sm text-zinc-400">No referrals yet — share your link to get started.</p> : (
        <div className="space-y-2">
          {data.referrals.map((r, i) => (
            <div key={i} className="flex justify-between text-sm border-b border-zinc-100 pb-2 text-zinc-800" data-testid={`referral-row-${i}`}>
              <span>{r.name} · joined {formatDate(r.joined_at)}</span>
              <span className={r.status === "rewarded" ? "text-emerald-600" : "text-zinc-400"}>{r.status === "rewarded" ? "Reward earned" : "Awaiting first order"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Submissions() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    api.get("/seller-submissions").then((res) => setSubs(res.data));
  }, []);
  return (
    <div data-testid="account-submissions">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-3xl text-zinc-900">
          Sell / Trade-In Submissions
        </h1>
        <Link to="/sell" className="text-xs underline font-heading uppercase text-teal-700">New Submission</Link>
      </div>
      {subs.length === 0 ? <p className="text-sm text-zinc-400">No submissions yet.</p> : (
        <div className="space-y-4">
          {subs.map((s) => (
            <div key={s.id} className="border border-zinc-200 rounded-sm bg-white p-4 flex justify-between items-center" data-testid={`submission-${s.submission_number}`}>
              <div>
                <div className="font-heading text-sm uppercase tracking-wide text-zinc-900">{s.submission_number} — {s.brand}</div>
                <div className="text-xs text-zinc-400">Status: {s.status.replace(/_/g, " ")}</div>
              </div>
              {["quote_sent", "revised_offer"].includes(s.status) && (
                <button onClick={() => navigate(`/offer/${s.id}`)} className="bg-teal-600 hover:bg-teal-700 text-ink text-xs px-4 py-2 font-heading font-semibold uppercase rounded-full transition-colors" data-testid={`view-offer-${s.submission_number}`}>
                  View Offer
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SavedSizes() {
  const [sizes, setSizes] = useState([]);
  const [form, setForm] = useState({ label: "My Glasses", lens_width: "", bridge_width: "", temple_length: "" });
  const load = () => api.get("/account/measurements").then((res) => setSizes(res.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/account/measurements", {
        label: form.label, lens_width: Number(form.lens_width), bridge_width: Number(form.bridge_width), temple_length: Number(form.temple_length),
      });
      setForm({ label: "My Glasses", lens_width: "", bridge_width: "", temple_length: "" });
      load();
      toast.success("Size saved");
    } catch (e2) {
      toast.error(apiErrorMessage(e2));
    }
  };

  const remove = async (id) => {
    await api.delete(`/account/measurements/${id}`);
    load();
  };

  return (
    <div data-testid="account-sizes">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Saved Sizes
      </h1>
      <p className="text-sm text-zinc-500 mb-6">Find the numbers printed inside your current glasses' arm — e.g. 52 □ 18 140 — and save them to filter frames that fit.</p>
      <form onSubmit={submit} className="border border-zinc-200 rounded-sm bg-white p-5 mb-8 flex flex-wrap gap-3 items-end" data-testid="add-size-form">
        <div>
          <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">Label</label>
          <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} className="bg-white border border-zinc-200 px-3 py-2 text-sm text-zinc-900 rounded-sm w-32" data-testid="size-label-input" />
        </div>
        <div>
          <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">Lens (mm)</label>
          <input required type="number" value={form.lens_width} onChange={(e) => setForm((f) => ({ ...f, lens_width: e.target.value }))} className="bg-white border border-zinc-200 px-3 py-2 text-sm text-zinc-900 rounded-sm w-20" data-testid="size-lens-input" />
        </div>
        <div>
          <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">Bridge (mm)</label>
          <input required type="number" value={form.bridge_width} onChange={(e) => setForm((f) => ({ ...f, bridge_width: e.target.value }))} className="bg-white border border-zinc-200 px-3 py-2 text-sm text-zinc-900 rounded-sm w-20" data-testid="size-bridge-input" />
        </div>
        <div>
          <label className="text-xs text-zinc-400 font-heading uppercase block mb-1">Temple (mm)</label>
          <input required type="number" value={form.temple_length} onChange={(e) => setForm((f) => ({ ...f, temple_length: e.target.value }))} className="bg-white border border-zinc-200 px-3 py-2 text-sm text-zinc-900 rounded-sm w-20" data-testid="size-temple-input" />
        </div>
        <button type="submit" className="bg-teal-600 hover:bg-teal-700 text-ink text-xs px-5 py-2.5 font-heading font-semibold uppercase rounded-full transition-colors" data-testid="save-size-button">Save Size</button>
      </form>
      {sizes.length === 0 ? <p className="text-sm text-zinc-400">No saved sizes yet.</p> : (
        <div className="space-y-2">
          {sizes.map((m) => (
            <div key={m.id} className="flex justify-between items-center border-b border-zinc-100 pb-2 text-sm text-zinc-800" data-testid={`saved-size-${m.id}`}>
              <span>{m.label} — {m.lens_width}–{m.bridge_width}–{m.temple_length}mm</span>
              <button onClick={() => remove(m.id)} className="text-zinc-400 hover:text-rose-600" aria-label="Remove size" data-testid={`remove-size-${m.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SavedSearches() {
  const [params] = useSearchParams();
  const highlightId = params.get("highlight");
  const [searches, setSearches] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const load = () => api.get("/account/saved-searches").then((res) => setSearches(res.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (highlightId) {
      const el = document.querySelector(`[data-testid="saved-search-${highlightId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId, searches]);

  const remove = async (id) => {
    await api.delete(`/account/saved-searches/${id}`);
    toast.success("Saved search deleted");
    load();
  };

  const startEdit = (s) => { setEditingId(s.id); setEditLabel(s.label); };

  const saveEdit = async (id) => {
    if (!editLabel.trim()) return;
    try {
      await api.patch(`/account/saved-searches/${id}`, { label: editLabel.trim() });
      toast.success("Saved search updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const filterSummary = (filters) => Object.entries(filters).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ");

  return (
    <div data-testid="account-searches">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Saved Searches
      </h1>
      <p className="text-sm text-zinc-500 mb-8">We'll email you and show a notification here whenever a new frame arrives matching one of these searches.</p>
      {searches.length === 0 ? (
        <p className="text-sm text-zinc-400">No saved searches yet — save a filter combo from the Shop page.</p>
      ) : (
        <div className="space-y-3">
          {searches.map((s) => (
            <div key={s.id} className={`flex justify-between items-start border rounded-sm bg-white p-4 transition-colors ${highlightId === s.id ? "border-teal-500 ring-2 ring-teal-100" : "border-zinc-200"}`} data-testid={`saved-search-${s.id}`}>
              <div className="flex-1 min-w-0">
                {editingId === s.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit(s.id)}
                      className="border border-neutral-200 rounded-md px-3 py-1.5 text-sm focus:border-teal-600 outline-none flex-1"
                      data-testid={`edit-saved-search-input-${s.id}`}
                    />
                    <button onClick={() => saveEdit(s.id)} className="text-teal-600 hover:text-teal-700" aria-label="Save" data-testid={`save-saved-search-${s.id}`}><Check size={16} /></button>
                    <button onClick={() => setEditingId(null)} className="text-zinc-400 hover:text-zinc-600" aria-label="Cancel"><X size={16} /></button>
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-heading font-semibold text-zinc-900">{s.label}</div>
                    <div className="text-xs text-zinc-400 mt-1">{filterSummary(s.filters)}</div>
                  </>
                )}
              </div>
              {editingId !== s.id && (
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <button onClick={() => startEdit(s)} className="text-zinc-400 hover:text-teal-600" aria-label="Rename saved search" data-testid={`edit-saved-search-${s.id}`}>
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => remove(s.id)} className="text-zinc-400 hover:text-rose-600" aria-label="Remove saved search" data-testid={`remove-saved-search-${s.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/account/notifications").then((res) => setData(res.data));
    api.post("/account/notifications/read-all");
  }, []);

  return (
    <div data-testid="account-notifications">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Notifications
      </h1>
      <p className="text-sm text-zinc-500 mb-8">New matches from your saved searches.</p>
      {!data ? <p className="text-sm text-zinc-400">Loading…</p> : data.notifications.length === 0 ? (
        <p className="text-sm text-zinc-400">No notifications yet.</p>
      ) : (
        <div className="space-y-4">
          {data.notifications.map((n) => (
            <div key={n.id} className="border border-zinc-200 rounded-sm bg-white p-4" data-testid={`notification-${n.id}`}>
              <div className="flex items-center gap-2 text-sm font-heading font-semibold text-zinc-900 mb-2">
                <Bell size={14} className="text-teal-600" /> New matches for "{n.saved_search_label}"
              </div>
              <div className="flex flex-wrap gap-3">
                {n.products.map((p) => (
                  <Link key={p.id} to={`/product/${p.slug}`} className="text-xs text-teal-700 hover:underline" data-testid={`notification-product-${p.id}`}>
                    {p.brand_name} {p.model} — ${p.selling_price}
                  </Link>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                <div className="text-[11px] text-zinc-400">{formatDate(n.created_at)}</div>
                {n.saved_search_id && (
                  <Link to={`/account/searches?highlight=${n.saved_search_id}`} className="inline-flex items-center gap-1 text-[11px] font-heading font-bold uppercase tracking-wide text-teal-700 hover:text-teal-800 transition-colors" data-testid={`notification-manage-search-${n.id}`}>
                    <Pencil size={12} /> Manage This Search
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Compare() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    api.get("/wishlist").then((res) => setItems(res.data));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) { toast("Compare up to 3 frames at once"); return prev; }
      return [...prev, id];
    });
  };

  const chosen = items.filter((p) => selected.includes(p.id));
  const rows = [
    { label: "Price", render: (p) => money(p.selling_price) },
    { label: "RRP", render: (p) => (p.rrp_verified !== false && p.original_rrp ? money(p.original_rrp) : "—") },
    { label: "Condition", render: (p) => `${p.condition_score?.toFixed ? p.condition_score.toFixed(1) : p.condition_score}/10 ${p.condition_label || ""}` },
    { label: "Lens width", render: (p) => (p.lens_width ? `${p.lens_width}mm` : "—") },
    { label: "Bridge", render: (p) => (p.bridge_width ? `${p.bridge_width}mm` : "—") },
    { label: "Temple", render: (p) => (p.temple_length ? `${p.temple_length}mm` : "—") },
    { label: "Material", render: (p) => p.frame_material || "—" },
    { label: "Colour", render: (p) => p.frame_colour || "—" },
    { label: "Shape", render: (p) => p.shape || "—" },
  ];

  return (
    <div data-testid="account-compare">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Compare Frames
      </h1>
      <p className="text-sm text-zinc-500 mb-8">Pick 2–3 frames from your wishlist to compare measurements, price and condition side by side.</p>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-400">Save frames to your wishlist first, then compare them here.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-8">
            {items.map((p) => (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                className={`px-3 py-2 rounded-full text-xs font-heading font-bold uppercase tracking-wide transition-colors ${selected.includes(p.id) ? "bg-charcoal text-white" : "bg-[#F8F8F9] text-neutral-600 hover:bg-neutral-200"}`}
                data-testid={`compare-select-${p.id}`}
              >
                {p.brand_name} {p.model}
              </button>
            ))}
          </div>

          {chosen.length < 2 ? (
            <p className="text-sm text-zinc-400" data-testid="compare-hint">Select at least 2 frames to compare.</p>
          ) : (
            <div className="overflow-x-auto" data-testid="compare-table">
              <table className="w-full min-w-[480px] border-collapse">
                <thead>
                  <tr>
                    <th className="w-28" />
                    {chosen.map((p) => (
                      <th key={p.id} className="p-3 align-bottom text-left">
                        <Link to={`/product/${p.slug}`} className="block" data-testid={`compare-header-${p.id}`}>
                          <div className="aspect-[4/3] bg-[#F2F2F4] rounded-xl overflow-hidden mb-2">
                            <img src={p.images?.[0]?.url} alt="" className="w-full h-full object-contain scale-110" />
                          </div>
                          <div className="font-heading font-extrabold text-sm text-neutral-900">{p.brand_name}</div>
                          <div className="text-xs text-neutral-500">{p.model}</div>
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.label} className="border-t border-neutral-100">
                      <td className="p-3 text-xs font-heading uppercase tracking-wide text-neutral-400">{r.label}</td>
                      {chosen.map((p) => (
                        <td key={p.id} className="p-3 text-sm font-mono text-neutral-800" data-testid={`compare-${r.label.toLowerCase().replace(/\s+/g, "-")}-${p.id}`}>{r.render(p)}</td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-t border-neutral-100">
                    <td className="p-3 text-xs font-heading uppercase tracking-wide text-neutral-400">Authenticity</td>
                    {chosen.map((p) => (
                      <td key={p.id} className="p-3 text-xs text-teal-700 font-heading font-bold uppercase tracking-wide">
                        <span className="inline-flex items-center gap-1"><ShieldCheck size={12} /> Checked</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FramePassports() {
  const [passports, setPassports] = useState(null);
  useEffect(() => {
    api.get("/account/frame-passports").then((res) => setPassports(res.data));
  }, []);
  return (
    <div data-testid="account-passports">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">
        Your Frame Passports
      </h1>
      <p className="text-sm text-zinc-500 mb-8">The inspection record and certificate for every pair you've purchased from Lookers.</p>
      {!passports ? <p className="text-sm text-zinc-400">Loading…</p> : passports.length === 0 ? (
        <p className="text-sm text-zinc-400">Your Frame Passports will appear here after your first purchase.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-6">
          {passports.map((p) => (
            <FramePassport key={p.product.id} product={p.product} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  return (
    <div className="container-lookers py-12 flex flex-col lg:flex-row gap-12" data-testid="account-page">
      <AccountNav />
      <div className="flex-1">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="orders" element={<Orders />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="compare" element={<Compare />} />
          <Route path="sizes" element={<SavedSizes />} />
          <Route path="searches" element={<SavedSearches />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="passports" element={<FramePassports />} />
          <Route path="photos" element={<MyPhotos />} />
          <Route path="credits" element={<Credits />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="submissions" element={<Submissions />} />
        </Routes>
      </div>
    </div>
  );
}

function MyPhotos() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/account/reviews").then((res) => setItems(res.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="account-photos">
      <h1 className="font-serif text-3xl mb-2 text-zinc-900">My Photos &amp; Reviews</h1>
      <p className="text-sm text-zinc-500 mb-8">Share a photo of you wearing your Lookers frames and we'll send you 15% off your next pair.</p>
      <Link to="/share-your-look" className="inline-flex items-center gap-2 bg-charcoal text-white text-xs font-heading font-bold uppercase tracking-wide px-6 py-3.5 rounded-full hover:bg-teal-600 transition-colors mb-8" data-testid="account-share-photos-link">
        <Camera size={14} /> Share Your Look
      </Link>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-400" data-testid="account-photos-empty">You haven't shared any photos yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="border border-zinc-200 rounded-xl bg-white p-5" data-testid={`account-review-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-heading font-bold text-sm text-zinc-900">{r.brand_name} {r.model}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{r.rating}/5 · {r.photos?.length || 0} photo{(r.photos?.length || 0) === 1 ? "" : "s"} · {formatDate(r.created_at)}</div>
                  {r.reward_coupon_code && (
                    <div className="text-xs font-mono font-bold text-teal-700 mt-2" data-testid={`account-review-code-${r.id}`}>
                      {r.reward_coupon_code} — 15% off your next pair
                    </div>
                  )}
                </div>
                <span className={`text-[10px] font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shrink-0 ${r.status === "approved" ? "bg-teal-50 text-teal-700" : r.status === "rejected" ? "bg-zinc-100 text-zinc-500" : "bg-amber-50 text-amber-700"}`}>
                  {r.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
